import type { OverviewViewConfig } from "@/lib/types";

export function configsEqual(a: OverviewViewConfig, b: OverviewViewConfig): boolean {
  // Stable compare: PostgreSQL stores JSONB with alphabetized keys,
  // so when a freshly saved view comes back from the API its JSON
  // string doesn't match the in-memory config's insertion-order
  // string — `isDirty` would stay TRUE forever and the banner
  // couldn't dismiss. Sort keys on both sides.
  return canonicalJson(normalizeConfig(a)) === canonicalJson(normalizeConfig(b));
}

/** Drop fields that drift on their own so dirty-detection only fires
 *  on real user-driven changes:
 *    - `mode === "rolling"` is the implicit default; older views saved
 *      before the mode toggle existed have no `mode` key at all.
 *      Strip both representations so they compare equal.
 *    - `dateFrom` / `dateTo` are a snapshot for rolling presets — a
 *      "Last 7 days" view re-resolves to fresh dates each load.
 *      Keep them only when the saved range is authoritative:
 *      `preset === "custom"` (no formula to roll) or `mode === "static"`
 *      (user explicitly pinned the calendar). */
function normalizeConfig(c: OverviewViewConfig): OverviewViewConfig {
  const out: OverviewViewConfig = { ...c };
  // mode: rolling is implicit. Drop it so old views (no key) and new
  // ones (`mode: "rolling"`) compare equal.
  if (out.mode === "rolling" || out.mode === undefined) {
    delete out.mode;
  }
  // Concrete dates only matter for custom or static views.
  const lockExact = c.preset === "custom" || c.mode === "static";
  if (!lockExact) {
    delete out.dateFrom;
    delete out.dateTo;
  }
  return out;
}

function canonicalJson(v: unknown): string {
  return JSON.stringify(v, (_key, val) => {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(val as Record<string, unknown>).sort()) {
        out[k] = (val as Record<string, unknown>)[k];
      }
      return out;
    }
    return val;
  });
}
